/**
 * Upload rules shared by every surface that accepts a file.
 *
 * There are two of them — the landing Composer and the workspace drop
 * handler — and they disagreed on everything: the Composer collected files
 * and never uploaded them at all, while the workspace uploaded correctly but
 * hardcoded every result as an image node. Both facts below (what kind of
 * node a file becomes, and how big a file may be) belong in one place so the
 * two cannot drift again.
 */
import * as Y from 'yjs'

/** Canvas node type for an uploaded file. */
export type UploadKind = 'image' | 'video' | 'audio'

/**
 * Hard ceiling on a single upload, set by Cloud Run and not by us.
 *
 * Cloud Run caps an HTTP/1 request body at 32 MiB. Exceeding it returns a
 * plain-HTML `413 Request Entity Too Large` from the Google frontend — the
 * app never sees the request, so no application-level error handling can
 * improve the message. Verified against production: a 40 MB `.MOV` produced
 * 413 and the browser silently retried the POST four times over 160 seconds
 * while the canvas node sat on "running" forever.
 *
 * Held slightly under 32 MiB because multipart framing (boundaries, the
 * `Content-Disposition` headers, the optional `name` field) adds a few
 * hundred bytes on top of the file itself, and a file at exactly the limit
 * would fail for reasons the user cannot see.
 */
export const MAX_UPLOAD_BYTES = 31 * 1024 * 1024

/** MIME types the API accepts. Mirrors the allowlist in the upload route. */
const ACCEPTED = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
])

/**
 * Which kind of node this file should become.
 *
 * Driven by MIME type with an extension fallback: browsers do not always
 * populate `File.type` (a `.mov` dragged from some file managers arrives as
 * an empty string), and defaulting those to `image` is exactly the bug this
 * replaces — a 34-second video landing on the canvas as "Upload Image",
 * unwireable into a video editor.
 */
export function uploadKind(file: File): UploadKind {
  const type = (file.type || '').toLowerCase()
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('audio/')) return 'audio'
  if (type.startsWith('image/')) return 'image'

  const ext = file.name.toLowerCase().split('.').pop() ?? ''
  if (['mp4', 'mov', 'webm', 'm4v', 'avi', 'mkv'].includes(ext)) return 'video'
  if (['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg'].includes(ext)) return 'audio'
  return 'image'
}

/** The `out_*` field the uploaded path is written to, per kind. */
export function uploadOutputField(kind: UploadKind): 'out_image' | 'out_video' | 'out_audio' {
  return kind === 'video' ? 'out_video' : kind === 'audio' ? 'out_audio' : 'out_image'
}

/** Human-readable size, for error messages that have to mean something. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/**
 * Why this file cannot be uploaded, or null if it can.
 *
 * Checked before the request goes out. The alternative is what shipped: an
 * opaque 413 from the Google frontend, four automatic retries, and a node
 * stuck running with nothing in the UI to explain it.
 */
export function rejectionReason(file: File): string | null {
  if (file.size > MAX_UPLOAD_BYTES) {
    return `${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(
      MAX_UPLOAD_BYTES,
    )} per file — trim it, or export at a lower resolution, and try again.`
  }
  if (file.size === 0) {
    return `${file.name} is empty.`
  }
  const type = (file.type || '').toLowerCase()
  // An empty type is allowed through: uploadKind() falls back to the
  // extension and the server does its own allowlist check.
  if (type && !ACCEPTED.has(type)) {
    return `${file.name} is a ${type} file, which isn't supported. Use PNG, JPEG, WebP, MP4, WebM, MOV, MP3 or WAV.`
  }
  return null
}

/**
 * An attachment the landing Composer already uploaded, handed to the
 * workspace through navigation state. Structurally the Composer's
 * `UploadedRef`; declared here so this module does not import a component.
 */
export interface ComposerUpload {
  name: string
  path: string
  kind: UploadKind
}

/**
 * Put the Composer's uploads on the canvas as completed upload nodes and
 * return their ids, for the Director's `referenced_node_ids`.
 *
 * The Composer uploaded these files and navigated here with the paths, and
 * then nothing read them: the bytes sat in project storage with no canvas
 * node, so a recipe that says "use my photo" reached the Director with no
 * photo it could see. This mirrors the workspace's own chat-attach path
 * (same node shape, same output field), minus the optimistic spinner — the
 * upload has already finished.
 *
 * Ids are derived from the attachment's position, so re-running this (a
 * refresh replays navigation state) finds the nodes already there instead of
 * stacking duplicates. Caller must wait for the YDoc to sync first, or the
 * existence check runs against an empty doc.
 */
export function placeComposerUploads(ydoc: Y.Doc, uploads: ComposerUpload[]): string[] {
  const yNodes = ydoc.getMap<Y.Map<unknown>>('nodes')
  const ids = uploads.map((u, i) => `${u.kind}_upload_composer_${i}`)
  ydoc.transact(() => {
    uploads.forEach((u, i) => {
      const nodeId = ids[i]
      if (yNodes.has(nodeId)) return
      const ynode = new Y.Map<unknown>()
      ynode.set('type', u.kind)
      ynode.set('label', u.name)
      ynode.set('status', 'completed')
      ynode.set('x', 80)
      ynode.set('y', 80 + i * 320)
      ynode.set('tainted', true)
      ynode.set('in_file_name', u.name)
      ynode.set(uploadOutputField(u.kind), u.path)
      yNodes.set(nodeId, ynode)
    })
  }, 'frontend')
  return ids
}
